import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useFetch, useScrollRestore } from '../../hooks/useFetch';
import { useToast, Spinner, ErrorBanner, ImportButton, ImportResult, EmptyState } from '../../components/ui';
import { fmt, errMsg } from '../../utils/format';
import api, { downloadFile } from '../../utils/api';
import AddBOMModal from './AddBOM';
import BOMDetailModal from './BOMDetail';

function BOMRow({ b, indent, setDetailId, saveScroll }) {
  return (
    <tr onClick={() => { saveScroll(); setDetailId(b.id); }} style={{ cursor: 'pointer' }}>
      <td></td>
      <td style={{ fontWeight: indent ? 400 : 500, paddingLeft: indent ? 16 : undefined }}>{b.product_name}</td>
      <td style={{ fontFamily:'monospace', fontSize:12 }}>{b.sku}</td>
      <td>{b.ingredient_count}</td>

    </tr>
  );
}

export default function BOM() {
  const { data, loading, error, refetch } = useFetch('/api/bom');
  const { showToast, Toast } = useToast();
  const navigate = useNavigate();
  const { saveScroll, restoreScroll, applyScroll } = useScrollRestore('sf_bom_scroll');
  useEffect(() => { applyScroll(); }, [data]); // eslint-disable-line
  const [search, setSearch] = useState(() => sessionStorage.getItem('sf_bom_search') || '');
  const [importResult, setImportResult] = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [pendingProductId, setPendingProductId] = useState(null);
  const [returnTo, setReturnTo] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('sf_bom_expanded') || '{}'); }
    catch { return {}; }
  });
  const toggleGroup = (key) => setExpandedGroups(e => {
    const next = { ...e, [key]: !e[key] };
    sessionStorage.setItem('sf_bom_expanded', JSON.stringify(next));
    return next;
  });

  // If arriving with ?product=ID, either open that product's existing BOM
  // or open the New BOM form pre-filled with it.
  useEffect(() => {
    const productParam = searchParams.get('product');
    const returnParam = searchParams.get('return');
    if (returnParam) setReturnTo(returnParam);
    if (!productParam || !data) return;
    const existing = data.find(b => String(b.product_id) === String(productParam));
    if (existing) {
      setDetailId(existing.id);
    } else {
      setPendingProductId(productParam);
      setAddModal(true);
    }
    // Clear the params so they don't re-trigger on refetch/close
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, data]);

  // Restore scroll position on return from another page handled by useScrollRestore hook.

  const closeAndReturn = () => {
    setAddModal(false);
    setDetailId(null);
    setPendingProductId(null);
    restoreScroll();
    if (returnTo && returnTo.startsWith('products-')) {
      const productId = returnTo.replace('products-', '');
      navigate(`/products?reopen=${productId}`);
    } else if (returnTo === 'products') {
      navigate('/products');
    }
  };

  const handleImport = async (file) => {
    setImporting(true); setImportResult(null);
    try {
      const form = new FormData(); form.append('file', file);
      const { data: res } = await api.post('/api/bom/import', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res); refetch();
    } catch (err) { setImportResult({ error: errMsg(err) }); }
    finally { setImporting(false); }
  };

  const filtered = (data || []).filter(b =>
    !search || b.product_name?.toLowerCase().includes(search.toLowerCase()) || b.sku?.toLowerCase().includes(search.toLowerCase())
  );

  // Group BOMs into collapsible rows by shared base product name — the text
  // before the first parenthetical variant suffix — same approach used on
  // Finished Goods and Kits & Bundles.
  const baseName = (name) => {
    if (!name) return '';
    const idx = name.indexOf('(');
    return idx > 0 ? name.slice(0, idx).trim() : name.trim();
  };

  const groups = React.useMemo(() => {
    const map = new Map();
    for (const b of filtered) {
      const key = `name:${baseName(b.product_name)}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(b);
    }
    const result = [];
    for (const [key, items] of map) {
      if (items.length > 1) {
        result.push({ isGroup: true, key, label: baseName(items[0].product_name), items });
      } else {
        result.push({ isGroup: false, key, items });
      }
    }
    const order = new Map(filtered.map((b, i) => [b.id, i]));
    result.sort((a, b) => order.get(a.items[0].id) - order.get(b.items[0].id));
    return result;
  }, [filtered]);

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} onRetry={refetch} />;
  return (
    <div>

      <div className="page-header">
        <div>
          <h1>Bill of Materials</h1>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-sm" onClick={() => {
              const allCollapsed = {};
              sessionStorage.setItem('sf_bom_expanded', JSON.stringify(allCollapsed));
              setExpandedGroups(allCollapsed);
            }}><i className="ti ti-fold" /> Collapse all</button>
            <button className="btn btn-sm" onClick={() => downloadFile('/api/bom/import/template')}><i className="ti ti-download" /> Template</button>
            <ImportButton onFile={handleImport} />
            <button className="btn btn-sm" onClick={() => downloadFile('/api/bom/export/csv')}><i className="ti ti-download" /> Export CSV</button>
            <button className="btn btn-primary btn-sm" onClick={() => setAddModal(true)}><i className="ti ti-plus" /> New BOM</button>
          </div>
        </div>
      </div>
      {addModal && <AddBOMModal onClose={closeAndReturn} onSaved={(msg) => { refetch(); showToast(msg || 'Saved'); }} initialProductId={pendingProductId} />}
      {detailId && <BOMDetailModal bomId={detailId} onClose={closeAndReturn} onSaved={(msg) => { refetch(); showToast(msg || 'Saved'); }} />}
      {importing && <div className="alert alert-info"><i className="ti ti-loader" /> Importing…</div>}
      {importResult && <ImportResult result={importResult} />}
      <div className="filter-bar">
        <div className="search-input"><i className="ti ti-search" /><input placeholder="Search by SKU or product…" value={search}
          onChange={e => { setSearch(e.target.value); sessionStorage.setItem('sf_bom_search', e.target.value); }} /></div>
      </div>
      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 28 }}></th>
                <th>Product</th><th>SKU</th><th>Parts</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => {
                if (!g.isGroup) {
                  return <BOMRow key={g.items[0].id} b={g.items[0]} setDetailId={setDetailId} saveScroll={saveScroll} />;
                }
                const expanded = !!expandedGroups[g.key];
                const totalIngredients = g.items.reduce((s, b) => s + (parseInt(b.ingredient_count) || 0), 0);
                const totalCost = g.items.reduce((s, b) => s + (parseFloat(b.total_material_cost) || 0), 0);
                return (
                  <React.Fragment key={g.key}>
                    <tr onClick={() => toggleGroup(g.key)} style={{ cursor: 'pointer', background: 'var(--bg-secondary)' }}>
                      <td>
                        <i className={`ti ${expanded ? 'ti-chevron-down' : 'ti-chevron-right'}`} style={{ fontSize: 12, color: 'var(--text-tertiary)' }} />
                      </td>
                      <td style={{ fontWeight: 600 }}>{g.label}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {g.items.length} variant{g.items.length !== 1 ? 's' : ''}
                      </td>
                      <td>{totalIngredients}</td>
                      <td>{fmt.currency(totalCost)}</td>
                      <td></td>
                    </tr>
                    {expanded && g.items.map(b => (
                      <BOMRow key={b.id} b={b} indent setDetailId={setDetailId} saveScroll={saveScroll} />
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {!filtered.length && <EmptyState icon="ti-list-tree" message="No BOMs found" />}
        </div>
      </div>
    </div>
  );
}
